import React from 'react';

import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import { useTimeTracker } from '@src/containers/App/TimeTracker/contexts/TimeTrackerProvider';
import { reportDtFactory } from '@src/containers/App/TimeTracker/Main/utils/reportDtFactory';
import ReportDtManager from '@src/containers/App/TimeTracker/Main/utils/ReportDtManager';

import { DateUtil } from '../dates';
import useGetParams from './useGetParams';
import { KIND_OF_REPORTS } from '@src/containers/App/TimeTracker/constants';

const REPORT_TYPE_ID = 'processType';
const PER_PAGE = 20;
const DATE_FORMAT = DateUtil.DEFAULT_DT_DATE_FORMAT;
/**
 * Manage the report data and the report generation through the ReportDtManager Class
 * @returns {{ renderReport: boolean, onSubmit: Function, data: Array }}
 */

/**
 * Manage the report data and the report generation through the ReportDtManager Class
 * @summary Manage the report data and the report generation
 * @returns {{ renderReport: boolean, onSubmit: Function, data: Array }}
 * @prop {boolean} renderReport - if the report should be rendered
 * @prop {Function} onSubmit - The function to run when the report is submitted
 * @prop {Array} data - The data of the report
 */
export default function useReportManagerDt() {
  const [renderReport, setRenderReport] = React.useState(false);
  const [data, setData] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [pagination, setPagination] = React.useState(0);
  const [error, setError] = React.useState(null);
  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const params = useGetParams();
  const { organizationIds } = useTimeTracker();

  const path = location.pathname;
  const reportType = path.split('/').pop();
  const body = Object.fromEntries(
    [REPORT_TYPE_ID, ...params.keys()].map(key => [
      key,
      params.get(key) === 'undefined' ? null : (params.get(key) ?? null),
    ]),
  );
  const { processType, ...reqBody } = body;

  const toDateOrNull = value => {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.valueOf()) ? null : value;
    if (typeof value === 'object') {
      const seconds = Number(value?.seconds ?? value?._seconds);
      const nanoseconds = Number(value?.nanoseconds ?? value?._nanoseconds ?? 0);
      if (Number.isFinite(seconds)) {
        const d = DateUtil.toDate(seconds, Number.isFinite(nanoseconds) ? nanoseconds : 0);
        return Number.isNaN(d.valueOf()) ? null : d;
      }
    }
    const ms = Number(value);
    if (!Number.isFinite(ms)) return null;
    const d = new Date(ms);
    return Number.isNaN(d.valueOf()) ? null : d;
  };

  const getOldestScheduledStartAt = React.useCallback(() => {
    if (!data || Object.keys(data).length === 0) return null;

    if (reportType === KIND_OF_REPORTS.MODIFICATIONS) {
      const firstEmployeeBucket = data?.aggregations?.by_employee_id?.buckets?.[0];
      const firstWeekBucket = firstEmployeeBucket?.by_week?.buckets?.[0];
      const firstAttendanceHit = firstWeekBucket?.shift_logs_docs?.hits?.hits?.[0];
      const source = firstAttendanceHit?._source ?? firstAttendanceHit?.source;
      const baseDateField = source?.createdAt;
      return toDateOrNull(baseDateField);
    } else if (reportType === KIND_OF_REPORTS.HOLIDAYS) {
      const firstEmployeeBucket = data?.aggregations?.by_employee_id?.buckets?.[0];
      const firstAttendanceHit = firstEmployeeBucket?.attendance_docs?.hits?.hits?.[0];
      const source = firstAttendanceHit?._source ?? firstAttendanceHit?.source;
      const baseDateField = source?.actualStartAt;
      return toDateOrNull(baseDateField);
    } else if (reportType === KIND_OF_REPORTS.ISSUES) {
      const firstAttendanceHit = data?.hits?.hits?.[0];
      const source = firstAttendanceHit?._source ?? firstAttendanceHit?.source;
      const baseDateField = source?.start_at;
      return toDateOrNull(baseDateField);
    } else {
      // attendance, daily
      const firstEmployeeBucket = data?.aggregations?.by_employee_id?.buckets?.[0];
      const firstWeekBucket = firstEmployeeBucket?.by_week?.buckets?.[0];
      const firstAttendanceHit = firstWeekBucket?.attendance_docs?.hits?.hits?.[0];
      const source = firstAttendanceHit?._source ?? firstAttendanceHit?.source;
      const baseDateField = source?.actualStartAt;
      return toDateOrNull(baseDateField);
    }
  }, [data, reportType]);

  // TODO: improve this function to return the period in the correct format for showing in the UI
  const getQueriedPeriod = () => {
    const { startDate, endDate, period } = reqBody;

    let start = toDateOrNull(startDate);
    let end = toDateOrNull(endDate);

    const days = Number(period);
    const now = new Date();
    if (Number.isFinite(days) && days > 0) {
      end = new Date(now);
      const startCandidate = new Date(now);
      startCandidate.setDate(startCandidate.getDate() - days);
      start = startCandidate;
    }

    const isValid = d => d instanceof Date && !Number.isNaN(d?.valueOf?.());

    let message = '';

    if (!isValid(start) && !isValid(end)) {
      const inferredStart = getOldestScheduledStartAt();
      const endDate = DateUtil.toDateString(now, DATE_FORMAT);
      if (isValid(inferredStart)) {
        const startDate = DateUtil.toDateString(inferredStart, DATE_FORMAT);
        return `${intl.formatMessage({ id: 'period.date.from' }, { start: startDate })}  ${intl.formatMessage(
          { id: 'period.date.to' },
          { end: endDate },
        )}`;
      }
      return intl.formatMessage({ id: 'period.date.to' }, { end: endDate });
    }

    if (isValid(start)) {
      const startDate = DateUtil.toDateString(start, DATE_FORMAT);
      message = intl.formatMessage(
        { id: 'period.date.from' },
        {
          start: startDate,
        },
      );
    }
    if (isValid(end)) {
      const endDate = DateUtil.toDateString(end, DATE_FORMAT);
      message = `${message}  ${intl.formatMessage(
        { id: 'period.date.to' },
        {
          end: endDate,
        },
      )}`;
    }
    return message;
  };

  const handleReport = new ReportDtManager(null, body.processType, {
    ...reqBody,
    orgIds: organizationIds,
    reportType,
  });

  const onSubmit = values => {
    const url = reportDtFactory(values.processType, values.values);
    history.push(url);
    setRenderReport(true);
  };

  const nextPage = async () => {
    setLoading(true);
    const newPage = pagination + PER_PAGE;
    const res = await handleReport.generateReport(newPage);
    if (res.success) {
      setData(res.data);
      setPagination(prev => prev + newPage);
    }
    setLoading(false);
  };

  /*
   * Fetch the report data when the page loads
   */
  React.useEffect(() => {
    if (renderReport) {
      const fetch = async () => {
        setLoading(true);
        try {
          // TODO: generate data and save it to useState
          const res = await handleReport.generateReport();
          if (res.success) {
            setData(res.data);
            setPagination(0);
          } else {
            setError(res.error);
          }
        } finally {
          setLoading(false); // This will now execute after the async operation completes
        }
      };
      fetch();
    } else {
      // Clear the data when the report is not rendered
      setData({});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderReport, organizationIds]);

  // This is to handle the case when the user is on the report page and the report is not rendered
  React.useEffect(() => {
    if (!location.search) {
      setRenderReport(false);
      setError(null);
    } else {
      setRenderReport(true);
    }
  }, [location.pathname, location.search]);

  return {
    renderReport,
    onSubmit,
    // TODO: return here de report data
    data,
    next: nextPage,
    loading,
    queriedPeriod: getQueriedPeriod(),
    error,
  };
}
